import React, { useMemo, useState } from 'react';

// 手動で出席を記録する（Web）【2026-10-01】
//   先生から「Web に手動で出席を取る画面が無い」。アプリと同じ POST /api/attendance/manual を使う。
//   ・回を選ぶ（今日の回が無ければ「今日」を選ぶとサーバーが回を作る）
//   ・学生は名簿から選ぶか、学籍番号を入れる（表記ゆれはサーバーが吸収する）
//   ・名簿に無い学生は、確認してから履修者に加えて記録する（enroll: true）
const STATUS_OPTIONS = [
  ['present', '出席'], ['late', '遅刻'], ['early_leave', '早退'], ['absent', '欠席'],
];
const LABEL = { present: '出席', late: '遅刻', early_leave: '早退', absent: '欠席', excused: '公欠', irregular: '要確認' };

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ManualAttendancePanel({ classId, sessions = [], students = [], auth, reload, setErr, setMsg }) {
  const [open, setOpen]         = useState(false);
  const [sessionKey, setSession] = useState('');
  const [who, setWho]           = useState('');      // users.id（名簿から）
  const [typed, setTyped]       = useState('');      // 学籍番号（手入力）
  const [status, setStatus]     = useState('present');
  const [reason, setReason]     = useState('');
  const [busy, setBusy]         = useState(false);

  const today = todayYmd();
  // 新しい回が上に来るように
  const sessionList = useMemo(() => [...sessions].reverse(), [sessions]);
  const hasToday = sessions.some(s => String(s.date).slice(0, 10) === today);
  const selectedKey = sessionKey || (sessionList[0] ? String(sessionList[0].id) : 'today');
  const picked = students.find(s => String(s.id) === String(who));
  const current = picked && selectedKey !== 'today' ? (picked.cells || {})[selectedKey] : null;

  const send = async (enroll) => {
    const body = { status, reason: reason.trim() || undefined };
    if (selectedKey === 'today') { body.classId = Number(classId); body.date = today; }
    else body.sessionId = Number(selectedKey);
    if (who) body.userId = Number(who);
    else body.studentId = typed.trim();
    if (enroll) body.enroll = true;
    const res = await fetch('/api/attendance/manual', { method: 'POST', headers: auth(), body: JSON.stringify(body) });
    return res.json();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!who && !typed.trim()) { setErr('学生を選ぶか、学籍番号を入れてください'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      let d = await send(false);
      if (!d.success && d.code === 'NOT_ENROLLED' && d.canEnroll) {
        const s = d.student || {};
        const okAdd = window.confirm(
          `${s.studentId || ''} ${s.name || ''} さんは、この授業の履修者名簿にいません。\n` +
          '履修者に加えて、出席を記録しますか？');
        if (!okAdd) return;
        d = await send(true);
      }
      if (!d.success) throw new Error(d.message || '記録に失敗しました');
      const a = d.attendance || {};
      setMsg(`${a.student_id || ''} の出席を「${LABEL[a.status] || a.status}」で記録しました` +
             (d.enrolled ? '（履修者に追加しました）' : ''));
      setTyped(''); setReason('');
      reload();
    } catch (err) { setErr(err.message); }
    finally { setBusy(false); }
  };

  const fmtSession = (s) => `第${s.no}回 ${String(s.date).slice(5, 10).replace('-', '/')}`;
  const pickable = students;

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap',
                                            ...(open ? {} : { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }) }}>
        <h2 style={{ margin: 0 }}>手動で出席を記録</h2>
        {!open && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>打刻できなかった学生の出席を、先生が記録します</span>}
        <button type="button" className="btn-secondary" aria-expanded={open}
                style={{ marginLeft: 'auto', padding: '0.2rem 0.7rem', fontSize: '0.85rem' }}
                onClick={() => setOpen(!open)}>
          {open ? '▲ 閉じる' : '▼ 開く'}
        </button>
      </div>
      {open && (
        <form onSubmit={submit} style={{ display: 'grid', gap: '0.6rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', alignItems: 'end' }}>
          <label style={{ fontSize: '0.8rem' }}>回
            <select value={selectedKey} onChange={e => setSession(e.target.value)} style={{ width: '100%' }}>
              {!hasToday && <option value="today">今日（{today.slice(5).replace('-', '/')}・回が無ければ作る）</option>}
              {sessionList.map(s => <option key={s.id} value={String(s.id)}>{fmtSession(s)}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.8rem' }}>学生（名簿から）
            <select value={who} onChange={e => { setWho(e.target.value); if (e.target.value) setTyped(''); }} style={{ width: '100%' }}>
              <option value="">— 学籍番号で入れる —</option>
              {pickable.map(s => (
                <option key={s.id} value={String(s.id)}>
                  {s.student_id} {s.name}{s.is_outside_roster ? '（名簿外）' : ''}
                </option>
              ))}
            </select>
          </label>
          {!who && (
            <label style={{ fontSize: '0.8rem' }}>学籍番号
              <input value={typed} onChange={e => setTyped(e.target.value)} placeholder="例: b250535" style={{ width: '100%' }} />
            </label>
          )}
          <label style={{ fontSize: '0.8rem' }}>状態
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: '100%' }}>
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.8rem' }}>理由（任意）
            <input value={reason} onChange={e => setReason(e.target.value)} maxLength={200}
                   placeholder="例: アプリが動かなかった" style={{ width: '100%' }} />
          </label>
          <div>
            <button className="btn-primary" type="submit" disabled={busy}>{busy ? '記録中…' : '記録する'}</button>
          </div>
          {picked && selectedKey !== 'today' && (
            <div style={{ gridColumn: '1 / -1', fontSize: '0.8rem', color: '#555' }}>
              この回のいまの記録：<strong>{current ? (LABEL[current] || current) : 'なし（欠席）'}</strong>
              {current && '（記録すると上書きします）'}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
